import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fileUrl } from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { TraceDialog } from "@/components/TraceDialog";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { FileCheck2, Search, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

const fields = [
  { name: "product_name", label: "Product / Chemical Name", required: true, full: true },
  { name: "supplier", label: "Supplier / Manufacturer", required: true },
  { name: "lot_number", label: "Lot Number", required: true, mono: true },
  { name: "ndc_or_cas", label: "NDC / CAS #", mono: true },
  { name: "date_received", label: "Date Received", type: "date", required: true },
  { name: "expiration_date", label: "Expiration Date", type: "date" },
  { name: "grade", label: "Grade / Purity" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "coa_file", label: "Attach COA (PDF or image)", type: "file", full: true, accept: "application/pdf,image/*" },
];

export default function COALibrary() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [traceLot, setTraceLot] = useState(null);

  const reload = useCallback(() => api.get("/coa_documents").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((r) =>
      [r.product_name, r.supplier, r.lot_number, r.ndc_or_cas, r.grade, r.notes]
        .some((v) => String(v || "").toLowerCase().includes(needle))
    );
  }, [items, q]);

  const columns = [
    { name: "date_received", label: "Received", mono: true },
    { name: "product_name", label: "Product", render: (r) => (
      <div>
        <div className="font-medium text-slate-800">{r.product_name}</div>
        {r.grade && <div className="text-xs text-slate-400">{r.grade}</div>}
      </div>
    ) },
    { name: "supplier", label: "Supplier" },
    { name: "lot_number", label: "Lot #", render: (r) => (
      r.lot_number ? (
        <button data-testid={`coa-trace-${r.id}`} onClick={() => setTraceLot(r.lot_number)}
          className="inline-flex items-center gap-1 font-mono text-xs text-pink-800 hover:text-pink-900">
          <Search className="w-3 h-3" /> {r.lot_number}
        </button>
      ) : <span className="text-slate-300">—</span>
    ) },
    { name: "ndc_or_cas", label: "NDC / CAS", mono: true },
    { name: "expiration_date", label: "Expires", mono: true },
    { name: "coa_file", label: "COA", render: (r) => (
      r.coa_file
        ? <Button data-testid={`coa-view-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1 text-pink-800"
            onClick={() => window.open(fileUrl(r.coa_file), "_blank")}><Eye className="w-3.5 h-3.5" /> View</Button>
        : <span className="text-slate-300">—</span>
    ) },
  ];

  return (
    <div>
      <PageHeader testid="coa-header" title="COA Library" subtitle="Certificates of Analysis by supplier, lot number and receive date — searchable in seconds.">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="coa-search-input" placeholder="Search product, supplier, lot, CAS…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-8 h-9 w-72" />
        </div>
      </PageHeader>
      <DataManager testidPrefix="coa" endpoint="coa_documents" fields={fields} columns={columns}
        items={filtered} reload={reload} emptyIcon={FileCheck2}
        emptyTitle={q ? "No COAs match your search" : "No COAs uploaded yet"} />
      <TraceDialog open={!!traceLot} onClose={() => setTraceLot(null)} lot={traceLot || ""} sections={["compounding_logs"]} />
    </div>
  );
}
